import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ClipboardCheck, Check, X, AlertCircle } from 'lucide-react'
import {
  getApprovalsInbox,
  verifyEvent,
  rejectEvent,
  approveExpense,
  rejectExpense,
  approveIncome,
  rejectIncome,
} from '../../services/api'

/**
 * Approvals Inbox — Session 3 of roles-permissions overhaul.
 *
 * Shows every pending approval_request the current user can vote on.
 * Item actions dispatch to the right cattle/finance endpoint based on
 * `target_type`, then invalidate the inbox query so the row disappears
 * once it resolves.
 *
 * Access is governed server-side: list_inbox_for_user only returns rows
 * whose required_role matches the caller's role_at_ranch (or all rows
 * for system_admin). The page itself is unprotected — non-approvers
 * just see an empty list.
 */
export default function ApprovalsInbox({ ranchId }) {
  const queryClient = useQueryClient()
  const [errorMsg, setErrorMsg] = useState(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['approvalsInbox', ranchId],
    queryFn: () => getApprovalsInbox(ranchId),
    refetchInterval: 30000,
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['approvalsInbox'] })
    queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    queryClient.invalidateQueries({ queryKey: ['pendingEventsCount'] })
  }

  const actOnRequest = async (item, decision) => {
    setErrorMsg(null)
    try {
      if (item.target_type === 'cattle_event') {
        if (decision === 'approve') {
          await verifyEvent(item.target_id, null, null)
        } else {
          await rejectEvent(item.target_id, null, 'Rejected from approvals inbox')
        }
      } else if (item.target_type === 'finance_expense') {
        if (decision === 'approve') await approveExpense(item.target_id)
        else await rejectExpense(item.target_id)
      } else if (item.target_type === 'finance_income') {
        if (decision === 'approve') await approveIncome(item.target_id)
        else await rejectIncome(item.target_id)
      }
      invalidate()
    } catch (e) {
      setErrorMsg(e.response?.data?.detail || e.message || 'Action failed')
    }
  }

  const voteMutation = useMutation({
    mutationFn: ({ item, decision }) => actOnRequest(item, decision),
  })

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse h-6 w-40 bg-earth-200 rounded mb-4" />
        <div className="animate-pulse h-20 bg-earth-100 rounded mb-2" />
        <div className="animate-pulse h-20 bg-earth-100 rounded mb-2" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6 text-red-600">
        Failed to load approvals: {error.message}
      </div>
    )
  }

  const items = data?.items || []

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <ClipboardCheck className="h-7 w-7 text-primary-600" />
        <h1 className="text-2xl font-semibold text-earth-900">Approvals Inbox</h1>
        <span className="ml-auto text-sm text-earth-500">
          {items.length} pending
        </span>
      </div>

      {errorMsg && (
        <div className="flex items-center gap-2 p-3 mb-4 rounded bg-red-50 text-red-700 text-sm">
          <AlertCircle className="h-4 w-4" />
          {errorMsg}
        </div>
      )}

      {items.length === 0 ? (
        <div className="p-8 text-center text-earth-500 bg-white rounded-lg border border-earth-200">
          <ClipboardCheck className="h-10 w-10 mx-auto mb-2 text-earth-300" />
          <p>Nothing to approve. Everything's caught up.</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((item) => (
            <li
              key={item.id}
              className="p-4 bg-white rounded-lg border border-earth-200 flex items-start gap-4"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs uppercase tracking-wider font-semibold text-earth-500">
                    {targetLabel(item.target_type)}
                  </span>
                  <span className="text-xs text-earth-400">
                    {item.ranch_name}
                  </span>
                  {item.policy !== 'first' && (
                    <span className="text-xs px-2 py-0.5 rounded bg-earth-100 text-earth-600">
                      {item.policy} • {item.approve_count}/{item.required_count}
                    </span>
                  )}
                </div>
                <p className="mt-1 text-earth-900 font-medium truncate">
                  {item.summary || '(no summary)'}
                </p>
                <p className="text-sm text-earth-500">
                  {item.amount ? `${item.amount} • ` : ''}
                  {item.date_display}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => voteMutation.mutate({ item, decision: 'approve' })}
                  disabled={voteMutation.isPending}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 text-sm"
                >
                  <Check className="h-4 w-4" />
                  Approve
                </button>
                <button
                  onClick={() => voteMutation.mutate({ item, decision: 'reject' })}
                  disabled={voteMutation.isPending}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-white border border-earth-300 text-earth-700 hover:bg-earth-50 disabled:opacity-50 text-sm"
                >
                  <X className="h-4 w-4" />
                  Reject
                </button>
                {item.target_type === 'cattle_event' && (
                  <Link
                    to="/cattle/events/pending"
                    className="text-xs text-earth-500 hover:text-primary-600 underline"
                  >
                    detail
                  </Link>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function targetLabel(tt) {
  switch (tt) {
    case 'cattle_event':    return 'Cattle event'
    case 'finance_expense': return 'Expense'
    case 'finance_income':  return 'Income'
    default:                return tt
  }
}
